import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Paper, Skeleton, Typography } from "@mui/material";
import { AssignmentOutlined, ShieldOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { DocumentList, ExpProcessRow, PaperConfigMissing, StepActionsExpediente } from "@components/ui_v2";
import PersonFormTabs from "@components/PersonForm/PersonFormTabs";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import { useLazyGetTabsConfigQuery } from "@api/catalogs";
import { useLazyGetDocumentAppIdQuery } from "@api/documents";
import { ApiError, BofuTabDocument, PersonaStructure } from "@interfaces/response";
import {
  applyPersonDocumentChange,
  buildPersonDocuments,
  filterPersonsForFormHub,
  findPersonasTab,
  PersonDocumentChange,
} from "@components/PersonForm/personHelpers";
import { validateDocumentStructure } from "@components/TabBofu/functions";
import {
  ALTO_SKELETON_EXPEDIENTE,
  BOFU_TABS_CONFIG,
  BOFU_TABS_FORM_CODES,
  PATHS_EXPEDIENTE_V2,
} from "@helpers/constantes";
import { isFormStatusEditable } from "@helpers/funciones";
import { UUID } from "crypto";

const tieneArchivo = (doc: BofuTabDocument) => (doc.multiple ? doc.list_values.some(Boolean) : Boolean(doc.file_value));

export const TabPersonas = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const applicationData = useSelector((state: RootState) => state.register.application);
  const [persons, setPersons] = useState<PersonaStructure[]>([]);
  const [documentsByPerson, setDocumentsByPerson] = useState<Record<string, BofuTabDocument[]>>({});
  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(null);
  const [isLoadingPersons, setIsLoadingPersons] = useState(true);
  const [isTabConfigMissing, setIsTabConfigMissing] = useState(false);
  const [hasPersonalForm, setHasPersonalForm] = useState(false);
  const [hasIdentityValidation, setHasIdentityValidation] = useState(false);

  const [getPersons] = useLazyGetPersonsQuery();
  const [getTabsConfig] = useLazyGetTabsConfigQuery();
  const [getDocumentsByAppId] = useLazyGetDocumentAppIdQuery();

  const selectedPerson = useMemo(
    () => persons.find((p) => p.id === selectedPersonId) || null,
    [persons, selectedPersonId],
  );

  const selectedDocuments = selectedPersonId ? documentsByPerson[selectedPersonId] || [] : [];

  const loadData = async () => {
    if (!applicationData?.id || !applicationData?.type) {
      setIsLoadingPersons(false);
      return;
    }

    try {
      setIsLoadingPersons(true);

      const [response, tabsConfig, uploadedDocuments] = await Promise.all([
        getPersons({ loanApplicationId: applicationData.id }).unwrap(),
        getTabsConfig({
          taxcode: applicationData.type,
          code: BOFU_TABS_CONFIG.tab_personas.code,
        }).unwrap(),
        getDocumentsByAppId({
          loanApplicationId: applicationData.id,
          status: true,
        }).unwrap(),
      ]);

      const personList = filterPersonsForFormHub(response || []);
      const personasTab = findPersonasTab(tabsConfig);

      if (!personasTab) {
        Sentry.captureMessage("No se encontró la configuración del tab de personas", {
          extra: { component: "TabPersonas-loadData", applicationId: applicationData.id },
        });
        setIsTabConfigMissing(true);
        return;
      }
      setIsTabConfigMissing(false);

      const tabDocuments = (personasTab.documents || []).filter(validateDocumentStructure);
      const forms = personasTab.forms || [];
      setHasPersonalForm(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.formularios_persona));
      setHasIdentityValidation(!!forms.find((f) => f.code === BOFU_TABS_FORM_CODES.validacion_identidad));

      const nextDocuments = Object.fromEntries(
        personList.map((person) => [
          person.id,
          buildPersonDocuments({
            person,
            tabDocuments,
            uploadedDocuments: uploadedDocuments || [],
            clientDetailsId: applicationData.client_details_id as UUID,
            allPersons: personList,
          }),
        ]),
      );

      setPersons(personList);
      setDocumentsByPerson(nextDocuments);
      setSelectedPersonId((current) => {
        if (current && personList.some((p) => p.id === current)) return current;
        return personList[0]?.id || null;
      });
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "TabPersonas-loadData" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      setPersons([]);
      setDocumentsByPerson({});
      setSelectedPersonId(null);
    } finally {
      setIsLoadingPersons(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationData?.id, applicationData?.type]);

  const handleDocumentChange = (change: PersonDocumentChange) => {
    if (!selectedPersonId) return;
    setDocumentsByPerson((current) => ({
      ...current,
      [selectedPersonId]: applyPersonDocumentChange(current[selectedPersonId] || [], change),
    }));
  };

  const handleOpenPersonalForm = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.formularioPersona.path}/${selectedPersonId}`);
  };

  const handleOpenIdentityValidation = () => {
    if (!selectedPersonId) return;
    navigate(`${PATHS_EXPEDIENTE_V2.validacionIdentidad.path}/${selectedPersonId}`);
  };

  const isLoading = isLoadingPersons;

  const todosCompletos = useMemo(
    () =>
      persons.length > 0 &&
      Object.values(documentsByPerson).every((docs) => docs.filter((d) => d.required).every(tieneArchivo)) &&
      persons.every((person) => {
        if (hasPersonalForm && person.personal_information_status !== "COMPLETO") return false;
        if (hasIdentityValidation && person.identity_validation_completed !== true) return false;
        return true;
      }),
    [persons, documentsByPerson, hasPersonalForm, hasIdentityValidation],
  );

  return (
    <Box>
      {isLoading ? (
        <Skeleton variant="rectangular" sx={{ height: ALTO_SKELETON_EXPEDIENTE, borderRadius: 2 }} />
      ) : isTabConfigMissing ? (
        <PaperConfigMissing />
      ) : persons.length === 0 ? (
        <Typography color="primary.dark" fontSize={16} textAlign="center" fontWeight={600} mt={3}>
          No se encontraron personas registradas
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          <PersonFormTabs persons={persons} selectedPersonId={selectedPersonId} onSelectPerson={setSelectedPersonId} />

          {selectedPerson && (
            <Fragment>
              {hasPersonalForm && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Información personal"
                    title="Llena tu información personal"
                    hint="Datos de contacto, domicilio y situación laboral"
                    status={selectedPerson.personal_information_status ?? "PENDIENTE"}
                    icon={<AssignmentOutlined sx={{ fontSize: 18 }} />}
                    label="Llenar"
                    onClick={handleOpenPersonalForm}
                    disabled={!isFormStatusEditable(selectedPerson.personal_information_status ?? "PENDIENTE")}
                    data-testid="expediente-personas-form-button"
                  />
                </Paper>
              )}

              {hasIdentityValidation && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <ExpProcessRow
                    heading="Verifica tu identidad"
                    hint="Selfie con prueba de vida + identificación oficial"
                    status={selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE"}
                    icon={<ShieldOutlined sx={{ fontSize: 18 }} />}
                    label="Validar"
                    onClick={handleOpenIdentityValidation}
                    disabled={
                      !isFormStatusEditable(
                        selectedPerson.identity_validation_completed === true ? "COMPLETO" : "PENDIENTE",
                      )
                    }
                    data-testid="expediente-personas-identidad-button"
                  />
                </Paper>
              )}

              {selectedDocuments.length > 0 && (
                <Paper variant="lendia_paper" sx={{ p: "20px 22px" }}>
                  <DocumentList
                    key={selectedPerson.id}
                    documents={selectedDocuments}
                    onChange={handleDocumentChange}
                    onChangeMultiple={handleDocumentChange}
                  />
                </Paper>
              )}
            </Fragment>
          )}

          <StepActionsExpediente
            currentTab={BOFU_TABS_CONFIG.tab_personas.code}
            nextDisabled={!todosCompletos}
            showRevision
          />
        </Box>
      )}
    </Box>
  );
};
